import { create } from 'zustand'
import type { CatServico } from '@/core/db/entities'
import { uuidv7 } from '@/lib/uuidv7'

/**
 * Estado da sessão de produção em construção, compartilhado entre Setup e Session.
 * Substitui `ProducaoGraphViewModel.kt`.
 *
 * Não persistimos no localStorage — quem persiste é o outbox (Dexie `out_producao`)
 * no `finalize()`. Se o usuário fechar o tab antes de finalizar, perde o rascunho;
 * mesma UX do Android (graph VM zerado ao sair do nav graph).
 */

export interface ApontamentoDraft {
  id: string // UUID v7 — vira o id da linha em out_producao
  servico: CatServico
  qtd: number | null
  // Material: vem do cadastro do serviço (servico.materialId). NÃO escolhido
  // pelo operador — campo read-only no form. Pode ser null se o serviço não
  // tem material vinculado.
  materialId: number | null
  materialNome: string | null
  materialQtd: number | null
  // Unidade do material: vem de cat_material.unidadeId (NÃO do serviço!).
  // Bug histórico era pegar a unidade do serviço — corrigido em 2026-05-27.
  materialUnidadeId: number | null
  materialUnidadeNome: string | null
  alturaMedia: number | null
  materialRedeId: number | null
  materialRedeNome: string | null
  // DN: picker da lista cadastrada no projeto (cdt_projeto_tem_diametro).
  // SIGA grava id + snapshot do nome em pnj_controle_producao.
  diametroId: number | null
  diametroNome: string | null
  tpRedeId: number | null
  tpRedeNome: string | null
  trechoId: number | null
  trechoNome: string | null
  estaca: string | null
  estacaInicial: string | null
  estacaFinal: string | null
  obs: string | null
}

export interface ProducaoSessionState {
  projetoId: number | null
  projetoCodigo: string
  dt: string // YYYY-MM-DD (today)
  equipeId: number | null
  equipeNome: string | null
  encarregadoId: number | null
  encarregadoNome: string | null
  chuva: boolean
  itens: ApontamentoDraft[]
}

interface ProducaoStore extends ProducaoSessionState {
  setProjeto: (id: number, codigo: string) => void
  setEncarregado: (id: number | null, nome: string | null) => void
  setEquipe: (id: number | null, nome: string | null) => void
  setDt: (dt: string) => void
  setChuva: (b: boolean) => void
  setItens: (itens: ApontamentoDraft[]) => void
  addOrUpdateItem: (item: ApontamentoDraft) => void
  removeItem: (id: string) => void
  reset: () => void
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

const initial: ProducaoSessionState = {
  projetoId: null,
  projetoCodigo: '',
  dt: todayIso(),
  equipeId: null,
  equipeNome: null,
  encarregadoId: null,
  encarregadoNome: null,
  chuva: false,
  itens: [],
}

export const useProducaoStore = create<ProducaoStore>((set) => ({
  ...initial,

  setProjeto: (id, codigo) =>
    set((s) =>
      s.projetoId === id
        ? { projetoCodigo: codigo }
        : {
            projetoId: id,
            projetoCodigo: codigo,
            // Trocar projeto invalida encarregado/equipe (vínculos são por projeto)
            equipeId: null,
            equipeNome: null,
            encarregadoId: null,
            encarregadoNome: null,
          },
    ),

  setEncarregado: (id, nome) =>
    set((s) =>
      s.encarregadoId === id
        ? {}
        : { encarregadoId: id, encarregadoNome: nome, equipeId: null, equipeNome: null },
    ),

  setEquipe: (id, nome) => set({ equipeId: id, equipeNome: nome }),

  setDt: (dt) => set({ dt }),

  setChuva: (chuva) => set({ chuva }),

  setItens: (itens) => set({ itens }),

  addOrUpdateItem: (item) =>
    set((s) => {
      const idx = s.itens.findIndex((i) => i.id === item.id)
      if (idx >= 0) {
        const next = s.itens.slice()
        next[idx] = item
        return { itens: next }
      }
      return { itens: [...s.itens, item] }
    }),

  removeItem: (id) => set((s) => ({ itens: s.itens.filter((i) => i.id !== id) })),

  reset: () => set({ ...initial, dt: todayIso() }),
}))

/**
 * Cria um draft novo. Pre-popula `material*` com o material default do serviço
 * (do `cat_servico` — backend manda `material` no DTO via JOIN). O operador
 * NÃO pode trocar o material no form — ele só preenche a quantidade.
 *
 * `materialUnidade*` vem do material vinculado (cat_material), não do serviço.
 * Se o material default tem unidadeId (devolvido pelo backend dentro do JOIN
 * de `material`), usa esse. Caso contrário fica null e o form não exibe
 * a unidade de material (operador não preenche qtd material).
 */
export function newApontamentoDraft(servico: CatServico): ApontamentoDraft {
  return {
    id: uuidv7(),
    servico,
    qtd: null,
    materialId: servico.materialId,
    materialNome: servico.materialNome,
    materialQtd: null,
    materialUnidadeId: servico.materialUnidadeId,
    materialUnidadeNome: servico.materialUnidadeNome,
    alturaMedia: null,
    materialRedeId: null,
    materialRedeNome: null,
    diametroId: null,
    diametroNome: null,
    tpRedeId: null,
    tpRedeNome: null,
    trechoId: null,
    trechoNome: null,
    estaca: null,
    estacaInicial: null,
    estacaFinal: null,
    obs: null,
  }
}
